<template>
  <content-modal
    ref="algebraKitModal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    :primary-button-disabled="false"
    class="algebra-kit-modal"
    full-screen
    @primary-click="embed"
    @modal-hide-request="actionHideRequest"
  >
    <template #title>{{ modalTitle }}</template>
    <template #content>
      <modal-content>
        <overlay-loader v-if="isAlgebraKitLoading" />
        <div>
          <input-checkbox
            v-if="isScorable"
            id="show-hint-and-feedback-checkbox"
            v-model:selected-values="availableShowHintAndFeedbackCheckboxValues"
            kind="white"
            value="true"
            @change="handleToggleShowHintAndFeedBack"
          >
            {{ t("algebra_kit_assessment_mode_description") }}
          </input-checkbox>
          <akit-interaction-editor
            v-if="isExerciseReady && isScorable"
            id="algebraKitComponent"
            :ref-id="refId"
            :exercise-id="exerciseId"
          />
          <akit-element-editor
            v-else-if="isExerciseReady && !isScorable"
            id="algebraKitComponent"
            :ref-id="refId"
            :exercise-id="exerciseId"
          />
          <cv-inline-notification
            v-if="hasAlgebraKitError || !isAlgebraKitResponseValid"
            kind="error"
            low-contrast
            :title="errorMessageContent"
            hide-close-button
            class="algebra-kit-error"
          />
          <akit-exercise-preview v-if="isExerciseReady" :exercise-id="exerciseId" :ref-id="refId" />
          <empty-placeholder v-if="!isExerciseReady && !isAlgebraKitLoading">
            <template #icon>
              <EmptyError />
            </template>
            <template #default>
              {{ t("algebra_kit_error_placeholder_message") }}
            </template>
          </empty-placeholder>
        </div>

        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="actionHideRequest">
            {{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            :disabled="hasAlgebraKitError"
            :error="hasAlgebraKitError"
            :loading="isLoading"
            @click="embed()"
          >
            <span v-if="hasAlgebraKitError">
              {{ t("button_state_error") }}
            </span>
            <span v-else>{{ t("button_save") }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import modalEventBus from "@plugins/modal/service/event-bus";
import randomUtils from "@utils/random.utils";
import { cookieManager } from "@utils/cookie.manager";
import appConfigs from "@configs/app";
import EmptyError from "@images/placeholder/empty-states/EmptyError.svg?component";
import { isEmpty } from "@utils/var.check";
import ScrollUtils from "@utils/scroll.utils";
import { AlgebraKitEventBus } from "@plugins/richtext/service/algebrakit.event.bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import RandomUtils from "@utils/random.utils";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { useVm } from "@utils/vm.ts";

interface Params {
  showFeedback?: boolean;
  refId?: string;
  isScorable: boolean;
  spec?: unknown;
  eventBusWatchInstances?: unknown;
}

const { vmNode } = useVm();

const props = withDefaults(defineProps<{ params?: Params }>(), {
  params: () => ({ isScorable: true }),
});

const id = ref<string>(randomUtils.randomId());
const autoHideOff = ref(true);
const isLoading = ref(false);
const availableShowHintAndFeedbackCheckboxValues = ref<string[]>(["true"]);
const showHintAndFeedback = ref<boolean>(props.params?.showFeedback ?? true);
const size = ref("large");
const closeAriaLabel = ref("Close");
const visible = ref(true);
const refId = ref<string>(props.params?.refId ?? "");
const exerciseId = ref<string>("");
const isAlgebraKitLoading = ref(true);
const hasAlgebraKitError = ref(false);
const isAlgebraKitResponseValid = ref(true);
const isScorable = ref<boolean>(props.params?.isScorable ?? true);

const { t } = useI18n();

const { setDirty, removeDirty } = useDirty(id);

const isExerciseReady = computed(() => exerciseId.value !== "");
const errorMessageContent = computed(() => t("algebra_kit_add_title_invalid_warning"));
const modalTitle = computed(() =>
  isScorable.value ? t("algebra_kit_add_title") : t("algebra_kit_add_non_scorable_title"),
);

type CVEvent = { ["cv:reason"]?: string } & Record<string, unknown>;

function actionHideRequest(event?: CVEvent) {
  if (event?.["cv:reason"] !== "external-click") {
    visible.value = false;
    EventBusUtils.unWatchInstance(props.params?.eventBusWatchInstances);
    modalEventBus.$emit("modal-hidden", vmNode.key);
  }
}

function embed() {
  isLoading.value = true;
  removeDirty(id.value);
  exerciseManager.inspect().then((response) => {
    if (response.valid) {
      isAlgebraKitResponseValid.value = true;
      AlgebraKitEventBus.$emit("embed-algebra-kit", {
        spec: exerciseManager.getExercise(),
        showHintAndFeedback: showHintAndFeedback.value,
        hasRandomised: response.random,
        refId: refId.value,
      });
      visible.value = false;
      EventBusUtils.unWatchInstance(props.params?.eventBusWatchInstances);
    } else {
      hasAlgebraKitError.value = true;
      isAlgebraKitResponseValid.value = false;
      setTimeout(() => {
        hasAlgebraKitError.value = false;
      }, 5000);
      handleAnchorToErrorMessage();
    }
  });

  isLoading.value = false;
}

function handleAnchorToErrorMessage() {
  nextTick(() => {
    const selector = document.querySelector(".algebra-kit-error");
    ScrollUtils.scrollToView(selector);
  });
}

function handleToggleShowHintAndFeedBack(value: string[]) {
  showHintAndFeedback.value = value.length === 1;
  setDirty(id.value);
}

interface ExerciseManager {
  inspect: () => Promise<{ valid: boolean; random: boolean }>;
  getExercise: () => unknown;
  getExerciseId: () => string;
  listElements: () => Record<string, unknown>;
  addElement: (index?: number, type?: string) => string;
  addInteraction: (index: number, name: string) => { id: string };
  removeElement: (index: number) => void;
  setExercise?: (spec: unknown, options: unknown) => Promise<ExerciseManager>;
}

let exerciseManager: ExerciseManager;

function isEditorReady(iteration: number): Promise<void> {
  if (iteration === 0) {
    isAlgebraKitLoading.value = false;
  }
  const editor = isScorable.value
    ? document.querySelector("akit-interaction-editor")
    : document.querySelector("akit-element-editor");
  return new Promise((resolve) => {
    if (editor) {
      if ((editor as HTMLElement).shadowRoot?.querySelector(".akit-exercise-editor-heading")) {
        resolve();
      } else {
        setTimeout(() => {
          const nextIteration = --iteration;
          isEditorReady(nextIteration).then(resolve);
        }, 2000);
      }
    } else {
      setTimeout(() => {
        const nextIteration = --iteration;
        isEditorReady(nextIteration).then(resolve);
      }, 2000);
    }
  });
}

function initEditor() {
  const defaultOptions = {
    availableAudiences: [
      { name: "English - Key Stage 5", id: "uk_KS5", languages: ["en"] },
      { name: "English - Key Stage 3", id: "uk_KS3", languages: ["en"] },
    ],
  };

  const spec = props.params?.spec;
  if (spec !== null) {
    window.AlgebraKIT.setExercise(spec, defaultOptions)
      .then((data: ExerciseManager) => {
        exerciseManager = data;
        exerciseId.value = (spec as { id: string }).id;

        if (isEmpty(refId.value)) {
          if (props.params?.isScorable) {
            exerciseManager.addElement(undefined, "QUESTION");
            const interactionName = RandomUtils.randomUuid();
            const interactionSpec = exerciseManager.addInteraction(
              Object.keys(exerciseManager.listElements()).length - 1,
              interactionName,
            );
            refId.value = interactionSpec.id;
          } else {
            refId.value = exerciseManager.addElement(undefined, "CONTENT");
          }
        }

        isEditorReady(5).then(() => {
          isAlgebraKitLoading.value = false;
        });
      })
      .catch((error: unknown) => {
        console.log(error);
        Promise.reject(error);
        isAlgebraKitLoading.value = false;
      });
  } else {
    window.AlgebraKIT.createExercise(defaultOptions)
      .then((data: ExerciseManager) => {
        exerciseManager = data;
        exerciseId.value = exerciseManager.getExerciseId();
        if (props.params?.isScorable) {
          const interactionName = RandomUtils.randomUuid();
          const interactionSpec = exerciseManager.addInteraction(0, interactionName);
          refId.value = interactionSpec.id;
        } else {
          exerciseManager.removeElement(0);
          refId.value = exerciseManager.addElement(undefined, "CONTENT");
        }

        isEditorReady(5).then(() => {
          isAlgebraKitLoading.value = false;
        });
      })
      .catch((error: unknown) => {
        console.log(error);
        Promise.reject(error);
        isAlgebraKitLoading.value = false;
      });
  }
}

onMounted(() => {
  if (!window.hasInitAKModal || !window.AlgebraKIT) {
    window.AlgebraKIT = {
      config: {
        proxy: { url: appConfigs.algebraKitProxy },
        secureProxy: {
          url: appConfigs.algebraKitProxy + "/secure",
          headers: {
            Authorization: `Bearer ${cookieManager.getCookie("jwt_token")}`,
          },
        },
        styles: {
          general: {
            "font-general": '"Noto Sans",Arial,sans-serif',
            "font-buttons": '"Noto Sans",Arial,sans-serif',
            "font-size-buttons": "small",
            "font-weight-buttons": "bold",
            "text-transform-buttons": "uppercase",
            "color-hint": "#1459cf",
            "color-error": "#86010D",
            "border-radius-buttons": "1em",
            "shadow-buttons": "none",
            "padding-buttons": "0.6em 1.5em",
            "height-form-controls": "40px",
            "border-radius-form-controls": "10px",
            "border-radius": "10px",
            "color-fg-buttons-default": "#1459CF",
            "color-bg-buttons-default": "transparent",
            "border-buttons-default": "1px solid #1459CF",
            "color-fg-buttons-default-hover": "white",
            "color-bg-buttons-default-hover": "#15499F",
            "border-buttons-default-hover": "1px solid #15499F",
            "color-fg-buttons-primary": "white",
            "color-bg-buttons-primary": "#1459CF",
            "border-buttons-primary": "1px solid #1459CF",
            "color-fg-buttons-primary-hover": "white",
            "color-bg-buttons-primary-hover": "#15499F",
            "border-buttons-primary-hover": "1px solid #15499F",
          },
          "formula-editor": {
            "color-primary": "#b6bdc5",
            "color-secondary": "#9da7b2",
            "color-sub-palette": "#0b233f",
            "color-tab-hover": "#546579",
            "color-palette-buttons-fg": "white",
            "color-palette-buttons-bg": "#0b233f",
            "border-palette-buttons": "1px solid white",
            "placeholder-color-main": "white", //TODO: doc
            "placeholder-color-sub": "white", //TODO: doc
          },
          interaction: {
            "padding-instruction": "1em",
            "border-radius-instruction-top": "10px",
            "border-radius-instruction-bottom": "10px",
            "border-instruction": "1px solid #ccc",
            "color-instruction-bg": "#efefef",
          },
          multistep: {
            "color-worksheet-bg": "white",
            "border-worksheet": "1px solid #ccc",
            "color-buttons-fg": "#0b233f",
            "color-buttons-fg-hover": "white",
            "color-buttons-bg": "white",
            "color-buttons-bg-hover": "#4f5a6d",
            "border-buttons": "1px solid #0b233f",
            "border-buttons-hover": "none",
            "border-radius-buttons": "1em",
            "color-hint-label": "var(--akit-color-hint)",
            "color-hint-main": "var(--akit-color-hint)",
            "color-hint-expr": "inherit",
            "color-hint-detail": "inherit",
            "color-fb-label": "var(--akit-color-error)",
            "color-fb-main": "var(--akit-color-error)",
            "color-fb-expr": "inherit",
            "color-fb-detail": "inherit",
            "color-buttons-fg-disabled": "white",
            "color-buttons-bg-disabled": "#cfd3d8",
            "border-buttons-disabled": "none",
          },
          derivation: {
            "color-step-bg": "#F5F7FC",
            "color-derivation-bg": "transparent",
            "shadow-step": "0 3px 5px 0 rgb(0 0 0 / 15%)",
          },
          "exercise-editor": {
            "color-bg-settings-button": "#485A6D",
            "color-step-buttons-active": "#1459CF",
            "color-bg-header": "white",
            "color-bg-balloon-title": "#C7CDD3",
            "color-fg-summary": "var(--akit-color-main-fg)",
            "color-bg-summary": "transparent",
            "border-collapsable-content": "none",
            "border-hr-step-buttons": "1px solid #dddddd",
            "border-bottom-summary": "1px solid #ddd",
            "padding-summary": "5px 0px",
            "color-bg-task-selector": "white",
            "color-fg-task-selector": "var(--akit-color-main-fg)",
          },
        },
        behavior: {
          components: {
            authoring: {
              multistep: { initialExpression: true },
            },
          },
        },
      },
    } as unknown;

    const scriptEl = document.createElement("script");
    scriptEl.setAttribute("id", "algebrakit");
    scriptEl.setAttribute("src", appConfigs.algebrakitPluginJs);
    scriptEl.onload = initEditor;
    document.head.appendChild(scriptEl);

    window.hasInitAKModal = true;
  } else {
    initEditor();
  }
  if (!isEmpty(props.params?.showFeedback) && !props.params?.showFeedback) {
    availableShowHintAndFeedbackCheckboxValues.value = [];
  }
});
</script>
